import { useEffect } from "react";

const ensureMeta = (selector, attributes) => {
    let element = document.head.querySelector(selector);
    const created = !element;
    if (!element) {
        element = document.createElement("meta");
        Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
        document.head.appendChild(element);
    }
    return { element, created };
};

const restoreMeta = ({ element, created }, previousContent) => {
    if (created) {
        element.remove();
    } else if (previousContent === null) {
        element.removeAttribute("content");
    } else {
        element.setAttribute("content", previousContent);
    }
};

export const clearSensitivePageRuntimeCaches = async () => {
    const cleanupTasks = [];

    if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
        cleanupTasks.push(navigator.serviceWorker.getRegistrations()
            .then((registrations) => Promise.allSettled(
                registrations.map((registration) => registration.unregister()),
            ))
            .catch(() => undefined));
    }

    if (typeof window !== "undefined" && window.caches?.keys) {
        cleanupTasks.push(window.caches.keys()
            .then((cacheNames) => Promise.allSettled(
                cacheNames.map((cacheName) => window.caches.delete(cacheName)),
            ))
            .catch(() => undefined));
    }

    await Promise.allSettled(cleanupTasks);
};

export const useSensitivePublicPage = (title) => {
    useEffect(() => {
        if (typeof document === "undefined" || typeof window === "undefined") {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = title;
        void clearSensitivePageRuntimeCaches();

        const referrerMeta = ensureMeta('meta[name="referrer"]', {
            name: "referrer",
            content: "no-referrer",
        });
        const previousReferrer = referrerMeta.element.getAttribute("content");
        referrerMeta.element.setAttribute("content", "no-referrer");

        const cacheMeta = ensureMeta('meta[http-equiv="Cache-Control"]', {
            "http-equiv": "Cache-Control",
            content: "no-store",
        });
        const previousCacheControl = cacheMeta.element.getAttribute("content");
        cacheMeta.element.setAttribute("content", "no-store");

        const robotsMeta = ensureMeta('meta[name="robots"]', {
            name: "robots",
            content: "noindex, nofollow, noarchive",
        });
        const previousRobots = robotsMeta.element.getAttribute("content");
        robotsMeta.element.setAttribute("content", "noindex, nofollow, noarchive");

        if (window.location.search || window.location.hash) {
            window.history.replaceState(
                window.history.state,
                document.title,
                window.location.pathname,
            );
        }

        return () => {
            document.title = previousTitle;
            restoreMeta(referrerMeta, previousReferrer);
            restoreMeta(cacheMeta, previousCacheControl);
            restoreMeta(robotsMeta, previousRobots);
        };
    }, [title]);
};
